import React, { useEffect, useState, useMemo } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import './ManageLeave.css';

export default function ManageLeave() {
    const { http, user } = AuthUser();
    const navigate = useNavigate();
    const [leaves, setLeaves] = useState([]);
    const [allLeaves, setAllLeaves] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterStaff, setFilterStaff] = useState(null);
    const [filterMonth, setFilterMonth] = useState('');
    const [filterDepartment, setFilterDepartment] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLeaveDetails, setSelectedLeaveDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);

    useEffect(() => {
        if (user && user.department_id !== 1) {
            toast.error("You don't have access to this page!");
            navigate('/dashboard');
            return;
        }
        fetchLeaves();
        fetchUsers();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [filterStatus, filterStaff, filterMonth, filterDepartment, allLeaves]);

    const fetchUsers = async () => {
        try {
            const response = await http.get(`/department_user_company?login_user=${user.id}`);
            if (response.data.users) {
                setUsers(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Extract unique departments from leaves data
    const extractDepartmentsFromLeaves = (leavesData) => {
        const deptMap = new Map();
        leavesData.forEach(leave => {
            if (leave.staff?.department?.id && leave.staff?.department?.name) {
                deptMap.set(leave.staff.department.id, {
                    id: leave.staff.department.id,
                    name: leave.staff.department.name
                });
            }
        });
        setDepartments(Array.from(deptMap.values()));
    };

    const fetchLeaves = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/leave_applied_all?login_user=${user.id}`);
            if (response.data.success) {
                setAllLeaves(response.data.data);
                setLeaves(response.data.data);
                // Extract departments from leave data
                extractDepartmentsFromLeaves(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching leaves:', error);
            toast.error('Error fetching leave applications');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filteredLeaves = [...allLeaves];
        
        if (filterDepartment) {
            filteredLeaves = filteredLeaves.filter(leave => 
                leave.staff?.department_id === filterDepartment.value
            );
        }
        
        if (filterStatus !== '') {
            filteredLeaves = filteredLeaves.filter(leave => 
                leave.is_approved === parseInt(filterStatus)
            );
        }
        
        if (filterStaff) {
            filteredLeaves = filteredLeaves.filter(leave => 
                leave.staff_id === filterStaff.value
            );
        }
        
        if (filterMonth) {
            const [year, month] = filterMonth.split('-');
            filteredLeaves = filteredLeaves.filter(leave => {
                const leaveDate = moment(leave.leave_applied);
                return leaveDate.year() === parseInt(year) && (leaveDate.month() + 1) === parseInt(month);
            });
        }
        
        setLeaves(filteredLeaves);
    };

    const fetchLeaveBalance = async (staffId) => {
        setLoadingDetails(true);
        try {
            const response = await http.get(`/leave_balance/${staffId}?login_user=${user.id}`);
            if (response.data.success) {
                setSelectedLeaveDetails(response.data.data);
                setShowDetailsModal(true);
            } else {
                toast.error('Failed to fetch leave details');
            }
        } catch (error) {
            console.error('Error fetching leave balance:', error);
            toast.error('Error fetching leave balance');
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleApprovalChange = async (leaveId, newStatus) => {
        try {
            const response = await http.put(`/leave_applied_approval/${leaveId}`, {
                is_approved: newStatus,
                login_user: user.id
            });
            
            if (response.data.success) {
                const statusText = newStatus === 1 ? 'Approved' : newStatus === 2 ? 'Rejected' : 'Pending';
                toast.success(`Leave ${statusText.toLowerCase()} successfully!`);
                fetchLeaves();
            } else {
                toast.error(response.data.message || 'Failed to update leave status');
            }
        } catch (error) {
            console.error('Error updating leave approval:', error);
            toast.error(error.response?.data?.message || 'Error updating leave status');
        }
    };

    const getStatusBadge = (status) => {
        if (status === 1) {
            return <span className="badge bg-success">Approved</span>;
        } else if (status === 2) {
            return <span className="badge bg-danger">Rejected</span>;
        } else {
            return <span className="badge bg-warning text-dark">Pending</span>;
        }
    };

    const getStatusColor = (status) => {
        if (status === 1) return 'table-success';
        if (status === 2) return 'table-danger';
        return 'table-warning';
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedLeaveDetails(null);
    };

    const clearFilters = () => {
        setFilterStatus('');
        setFilterStaff(null);
        setFilterMonth('');
        setFilterDepartment(null);
    };

    const userOptions = useMemo(() => {
        let filteredUsers = users;
        // Filter users by department if department filter is active
        if (filterDepartment) {
            filteredUsers = users.filter(u => u.department_id === filterDepartment.value);
        }
        return filteredUsers.map(u => ({
            value: u.id,
            label: u.name + (u.last_name ? ' ' + u.last_name : '')
        }));
    }, [users, filterDepartment]);

    const departmentOptions = useMemo(() => {
        return departments.map(d => ({
            value: d.id,
            label: d.name
        }));
    }, [departments]);

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: '0', label: 'Pending' },
        { value: '1', label: 'Approved' },
        { value: '2', label: 'Rejected' }
    ];

    const monthOptions = useMemo(() => {
        const options = [];
        const now = new Date();
        for (let i = -6; i <= 2; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            options.push({ value, label });
        }
        return options.reverse();
    }, []);

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                {/* Compact Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Manage Leave Requests</h5>
                    <span className="badge bg-secondary">{leaves.length} records</span>
                </div>

                {/* Compact Filters */}
                <div className="card mb-2">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Department</label>
                                <Select
                                    options={departmentOptions}
                                    value={filterDepartment}
                                    onChange={(opt) => {
                                        setFilterDepartment(opt);
                                        setFilterStaff(null); // Reset staff when department changes
                                    }}
                                    isClearable
                                    placeholder="All Dept"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '34px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Staff</label>
                                <Select
                                    options={userOptions}
                                    value={filterStaff}
                                    onChange={setFilterStaff}
                                    isClearable
                                    placeholder="All Staff"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '34px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Month</label>
                                <Select
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === filterMonth)}
                                    onChange={(opt) => setFilterMonth(opt ? opt.value : '')}
                                    isClearable
                                    placeholder="All"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '34px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small mb-1">Status</label>
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === filterStatus)}
                                    onChange={(opt) => setFilterStatus(opt ? opt.value : '')}
                                    placeholder="All"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '34px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                                    <i className="fa fa-times me-1"></i>Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : leaves.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No leave applications found</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Applied On</th>
                                            <th>Staff Name</th>
                                            <th>Department</th>
                                            <th>Leave Date</th>
                                            <th>Reason</th>
                                            <th>Report To</th>
                                            <th>Status</th>
                                            <th>Details</th>
                                            <th style={{ minWidth: '180px' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaves.map((leave, index) => (
                                            <tr key={leave.id} className={getStatusColor(leave.is_approved)}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <small>{moment(leave.created_at).format('DD MMM YY')}</small>
                                                </td>
                                                <td>
                                                    <strong style={{ fontSize: '13px' }}>
                                                        {leave.staff?.name || 'N/A'} 
                                                        {leave.staff?.last_name ? ' ' + leave.staff.last_name : ''}
                                                    </strong>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {leave.staff?.department?.name || '-'}
                                                    </small>
                                                </td>
                                                <td>
                                                    <strong style={{ fontSize: '13px' }}>{moment(leave.leave_applied).format('DD MMM YY')}</strong>
                                                </td>
                                                <td>
                                                    <div style={{ maxWidth: '150px', fontSize: '12px' }} className="text-truncate" title={leave.reason}>
                                                        {leave.reason || 'N/A'}
                                                    </div>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {leave.reportTo?.name || 'N/A'}
                                                </td>
                                                <td>{getStatusBadge(leave.is_approved)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-outline-info py-0"
                                                        onClick={() => fetchLeaveBalance(leave.staff_id)}
                                                        disabled={loadingDetails}
                                                        title="View Leave Balance"
                                                    >
                                                        <i className="fa fa-info-circle"></i>
                                                    </button>
                                                </td>
                                                <td>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        style={{ 
                                                            width: '110px', 
                                                            fontSize: '12px',
                                                            backgroundColor: leave.is_approved === 1 ? '#d4edda' : leave.is_approved === 2 ? '#f8d7da' : '#fff3cd',
                                                            color: '#000',
                                                            fontWeight: '500'
                                                        }}
                                                        value={leave.is_approved}
                                                        onChange={(e) => handleApprovalChange(leave.id, parseInt(e.target.value))}
                                                    >
                                                        <option value="0">Pending</option>
                                                        <option value="1">Approved</option>
                                                        <option value="2">Rejected</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Leave Details Modal */}
            {showDetailsModal && selectedLeaveDetails && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className="fa fa-user me-2"></i>
                                    Leave Details - {selectedLeaveDetails.user?.name || 'N/A'}
                                </h6>
                                <button type="button" className="btn-close" onClick={closeDetailsModal}></button>
                            </div>
                            <div className="modal-body">
                                {/* User Info */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-user me-2"></i>Employee Information</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p className="mb-1"><strong>Name:</strong> {selectedLeaveDetails.user?.name}</p>
                                                <p className="mb-0"><strong>Email:</strong> {selectedLeaveDetails.user?.email || '-'}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p className="mb-0"><strong>Phone:</strong> {selectedLeaveDetails.user?.phone || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Salary Master Info */}
                                {selectedLeaveDetails.salary_master && (
                                    <div className="card mb-3">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="fa fa-briefcase me-2"></i>Employee Details</h6>
                                        </div>
                                        <div className="card-body py-2">
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <p className="mb-1"><strong>Father Name:</strong> {selectedLeaveDetails.salary_master.father_name || '-'}</p>
                                                </div>
                                                <div className="col-md-4">
                                                    <p className="mb-1"><strong>DOB:</strong> {selectedLeaveDetails.salary_master.dob ? moment(selectedLeaveDetails.salary_master.dob).format('DD MMM YYYY') : '-'}</p>
                                                </div>
                                                <div className="col-md-4">
                                                    <p className="mb-1"><strong>Location:</strong> {selectedLeaveDetails.salary_master.location || '-'}</p>
                                                </div>
                                                <div className="col-md-4">
                                                    <p className="mb-1"><strong>Emp Type:</strong> 
                                                        <span className={`badge ms-1 ${selectedLeaveDetails.salary_master.emp_type === 'Permanent' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                            {selectedLeaveDetails.salary_master.emp_type || '-'}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Leave Summary */}
                                <div className="card mb-3">
                                    <div className="card-header" style={{ background: '#e3f2fd' }}>
                                        <h6 className="mb-0"><i className="fa fa-calendar-check me-2"></i>Leave Summary ({new Date().getFullYear()})</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row text-center g-2">
                                            <div className="col">
                                                <div className="p-2 bg-light rounded">
                                                    <h5 className="text-primary mb-0">{selectedLeaveDetails.leave_summary?.total_allowed || 0}</h5>
                                                    <small className="text-muted">Total</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="p-2 bg-light rounded">
                                                    <h5 className="text-info mb-0">{selectedLeaveDetails.leave_summary?.total_applied || 0}</h5>
                                                    <small className="text-muted">Applied</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="p-2 bg-light rounded">
                                                    <h5 className="text-success mb-0">{selectedLeaveDetails.leave_summary?.approved || 0}</h5>
                                                    <small className="text-muted">Approved</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="p-2 bg-light rounded">
                                                    <h5 className="text-warning mb-0">{selectedLeaveDetails.leave_summary?.pending || 0}</h5>
                                                    <small className="text-muted">Pending</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="p-2 bg-light rounded">
                                                    <h5 className="text-danger mb-0">{selectedLeaveDetails.leave_summary?.rejected || 0}</h5>
                                                    <small className="text-muted">Rejected</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="p-2 bg-success bg-opacity-25 rounded">
                                                    <h5 className="text-success mb-0">{selectedLeaveDetails.leave_summary?.remaining || 0}</h5>
                                                    <small className="text-muted">Remaining</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Applied Leaves History */}
                                {selectedLeaveDetails.applied_leaves && selectedLeaveDetails.applied_leaves.length > 0 && (
                                    <div className="card">
                                        <div className="card-header bg-light py-2">
                                            <h6 className="mb-0"><i className="fa fa-history me-2"></i>Leave History ({new Date().getFullYear()})</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-sm mb-0">
                                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Date</th>
                                                            <th>Reason</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedLeaveDetails.applied_leaves.map((leave, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{moment(leave.leave_applied).format('DD MMM YYYY')}</td>
                                                                <td>{leave.reason || '-'}</td>
                                                                <td>
                                                                    {leave.is_approved === 1 ? (
                                                                        <span className="badge bg-success">Approved</span>
                                                                    ) : leave.is_approved === 2 ? (
                                                                        <span className="badge bg-danger">Rejected</span>
                                                                    ) : (
                                                                        <span className="badge bg-warning text-dark">Pending</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer py-2">
                                <button type="button" className="btn btn-sm btn-secondary" onClick={closeDetailsModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
